// A1 — THE MONEY-TABLE CELLS. Brief 65 (brief 50 §4), the first of the sixteen.
//
// The three cell builders the financials table is made of, declared once
// instead of twice. They were `TH`/`MC`/`ML` inside `CaseView`'s financials
// section and `TH2`/`MC2`/`ML2` inside `CollectiveCaseView`'s, and the census
// reported all three pairs at 100.0%. The coordinates are as of THIS BRANCH'S
// BASE, `8fdbbd6c`, because this commit is what stops them being live — a
// coordinate naming where code was removed from is history the moment it is
// true:
//
//   8fdbbd6c CaseView.tsx:4968-4984 x CollectiveCaseView.tsx:10478-10494  the header cell
//   8fdbbd6c CaseView.tsx:4985-4999 x CollectiveCaseView.tsx:10495-10509  the money cell
//   8fdbbd6c CaseView.tsx:5000-5011 x CollectiveCaseView.tsx:10510-10521  the label cell
//
// Proved rather than assumed: the two 44-line regions diff EMPTY once `2` is
// stripped from the identifiers, so there is no behaviour to choose between
// them and no parameter to invent. Moved verbatim — the signatures are the
// union of what every call site passes, which here is the same signature both
// copies already had.
//
// The four `<tr>` blocks that also pair (at 8fdbbd6c: CaseView 5025-5044 x
// 5045-5064, CCV 10681-10700 x 10701-10720) are CALL SITES of these cells and stay where
// they are: a row of a table is not a mechanism with a name, and moving one
// would be a decision about the table rather than a move.
//
// Closure cost zero: `FS` and `fc` are module-scope imports in both views
// already (src/lib/tokens, src/utils), so nothing is handed across the seam.
import React from "react"; // 169 — react/react-in-jsx-scope is counted per JSX element; the rule's own remedy
import type { ReactNode } from "react";
import { fc, fmin } from "../utils";
import { FS } from "./tokens";
import MoneyStack from "../components/MoneyStack"; // 169 R7

// BRIEF 87 §5 — THE ADAPTIVE GAP, AND THE MECHANISM IS ONE STYLE ON ONE CELL.
//
// The flexible space fell between RP hours and everything after it. It must fall
// between the HOURS group and the FEES group:
//
//   label | RP hours · collaborator hours · total hours |◄ slack ►| fees only · fees+costs · fees+costs+VAT
//
// §5 is right that this cannot be done by changing an argument — `TH(l, r)`
// carried a right-align flag and no width — so the primitive is WIDENED rather
// than the behaviour bent (rule 4). `grow` is the third parameter.
//
// WHY A `width: 100%` HEADER CELL AND NOT A SPACER COLUMN. The table is
// `width: 100%` with the browser's automatic layout, where a column declared
// `width: 100%` absorbs every pixel of leftover space. The cell is ALSO
// right-aligned, so its own text stays hard against the fees group and the
// absorbed space opens on its LEFT — which is precisely the boundary asked for.
//
// A spacer <td> would have worked too and was rejected: it needs a cell added to
// the header AND to every body row in BOTH tables, so the count of cells per row
// becomes a thing to keep in step across two implementations that §0 deliberately
// keeps separate — and a mismatch renders as a silently shifted column rather
// than as an error. Explicit widths were rejected for the same reason plus a
// worse one: they are pixel guesses that go wrong at the first long translation.
// One flag on one header cell per table changes nothing else and cannot drift.
//
// Applied to the FIRST fees column, in both views. It is a header-only concern:
// body cells take their width from the column the header establishes.
export const TH = (l?: any, r?: any, grow?: boolean | 1 | 0) => (
  <th
    style={{
      fontSize: FS.s9_5,
      fontWeight: 600,
      color: "var(--tm)",
      textTransform: "uppercase",
      letterSpacing: ".3px",
      textAlign: r ? "right" : "left",
      padding: "5px 8px",
      borderBottom: "1px solid var(--br)",
      whiteSpace: "nowrap",
      ...(grow ? { width: "100%" } : {}),
    }}
  >
    {l}
  </th>
);
// 169 R7 — the fourth parameter is the cell's foreign half ({ USD: 400 }); a
// cell without one renders exactly what it did.
export const MC = (v?: any, c?: any, fw?: any, foreign?: Record<string, number> | null) => (
  <td
    className="mono"
    style={{
      textAlign: "right",
      padding: "5px 8px",
      color: c,
      fontWeight: fw,
      fontSize: FS.s11_5,
      whiteSpace: "nowrap",
    }}
  >
    {foreign ? <MoneyStack home={v} foreign={foreign} /> : (v != null ? fc(v) : "—")}
  </td>
);
// BRIEF 90 §2a — THE LABEL COLUMN GETS BREATHING ROOM. `Total`, `Billed`,
// `Unbilled`, `Labor Cost`, `Gross Margin`, `Paid` and `Balance due` sat hard
// against RP HOURS, which reads as one run of text rather than as a label and a
// figure.
//
// The gap is put on the LABEL cell's right rather than on the first hours
// column's left, and that is the load-bearing choice: `ML` IS the label cell —
// it exists for no other purpose and has twelve call sites, all of them this
// column in the two tables — whereas `HC` is shared by all three hours columns,
// so padding it would have opened three gaps to close one. The column's width is
// set by its widest cell, which is always a label, so this sets the gap.
//
// ONE EDIT AND NOT TWO, and that does not contradict §2's "both fixes land
// twice": what stays two implementations is the two TABLES. These cell builders
// were already deduplicated by brief 65 A1, long before this brief, so the label
// cell is one function and the honest place to change it is once.
// 206 §1 [RULED — Laurent's glance on 205 §1: in BY ACTIVITY a long name is
// ellipsised and its percentage vanishes with it] — THE FOURTH PARAMETER, AND
// THE CENSUS THAT NAMES WHAT IT UNDOES.
//
// `ML` never bound a width: it declares padding, colour, weight and size and
// nothing else. What binds it is the STYLESHEET, `tbody td` (App.css:836-846),
// four declarations acting together — `max-width: 28ch`, `overflow: hidden`,
// `text-overflow: ellipsis`, `white-space: nowrap` — over a table whose layout
// is `auto` (nothing in src/ sets `table-layout` on it) and whose width is the
// stylesheet's own `table { width: 100% }`. 205 §1 put a chip in front of the
// name inside the SAME span as the name and the percentage, so a row that used
// to fit 28ch stopped fitting, and the ellipsis cut the span's LAST child — the
// percentage — before it cut anything else.
//
// `wide` lifts that one cap, and only the cap: the other three declarations
// stay, so a cell still never wraps and still clips rather than spilling. With
// `max-width: none` the column's width becomes its content's, and the room
// comes from where the room already is — the same row's six figure columns are
// `HC(null)`/`MC(null)` on an activity row, and the fees header that carries
// `grow` (`width: 100%`, brief 87 §5) is the cell absorbing the slack the label
// column now takes back. A table's min-content widths are satisfied before any
// slack is distributed, so this takes room from the absorber and from nothing
// else; nothing on the row moves.
//
// NOT DONE HERE and said so: the cap is not lifted for `ML`'s other twenty call
// sites. Those are the recap's own labels — "Unmarked", "Due ex VAT",
// "Effective margin" — none of which reaches 28ch, so lifting it for them would
// change nothing today and would silently change something the day a
// translation grew. The escape hatch has a precedent with exactly this shape:
// `.elist-matters tbody td { max-width: none }` (brief 108 §9) lifts the same
// cap for the two matter lists and leaves every other list capped.
export const ML = (ch?: any, c?: any, fw?: any, wide?: boolean) => (
  <td
    style={{
      padding: "5px 18px 5px 8px",
      color: c || "inherit",
      fontWeight: fw,
      fontSize: FS.s11,
      ...(wide ? { maxWidth: "none" } : {}),
    }}
  >
    {ch}
  </td>
);

// HC — the HOURS cell. Brief 86 §2b: the Financial summary gains three columns
// before the fees columns, RP hours / collaborator hours / total hours, in BOTH
// views. It is MC's twin in every respect except the formatter — `fmin`, the
// app's own minutes rendering ("12h 30min"), which the same table already used
// inline beside the Total / Billed / Unbilled labels before those figures moved
// into a column of their own.
//
// It is dimmer than MC deliberately (var(--tm), not inherit): hours sit beside
// money in the same row and the money is the subject. `null` reads "—" exactly
// as MC does, which is what the rows carrying no hours (labor cost, gross
// margin, paid, balance) render.
export const HC = (m?: number | null, c?: string) => (
  <td
    className="mono"
    style={{
      textAlign: "right",
      padding: "5px 8px",
      color: c || "var(--tm)",
      fontSize: FS.s11_5,
      whiteSpace: "nowrap",
    }}
  >
    {m != null ? fmin(m) : "—"}
  </td>
);

// 199 §F — TWO MORE FACES AND TWO MORE CELLS, for the costs line the matter
// recap gains (CaseView / CollectiveCaseView, the two lines folded into the
// financial summary). `pctCo` and `ratCo` are Frais et honoraires' own faces
// (CostsView's `__pctCo` / `__ratCo`), MOVED here so the recap prints a rate
// or a share exactly as the case table does — « 362 €/h », « 76% », a computed
// 0 €/h as « — » (198 A.3a keeps that one dash) — and CostsView imports them
// back. Typed at the move (the ratchet's rule for a lib): a number or nothing.
// 214 addendum 1 §14 [RULED 58] — ROUNDED, NOT CUT: a share of 14,9 % read
// « 14% » and a rate of 362,7 €/h « 362 €/h » (`Math.floor`); both now round
// to the nearest whole. Every reader follows through this one definition
// (CaseView, CollectiveCaseView, CostsView — docs/214-deploy-note.md §7.14);
// a computed 0 €/h keeps its dash.
export const pctCo = (v: number | null | undefined): string => (v == null ? "—" : String(Math.round(v)) + "%");
export const ratCo = (v: number | null | undefined): string => (v == null || v === 0 ? "—" : String(Math.round(v)) + " €/h");
// 214 addendum 1 §11 [RULED 57] — THE MARGIN'S PERCENTAGE, ONE DECIMAL, IN THE
// READER'S NUMBER FORMAT: the platform's percent pattern for the reader's
// locale (`localeFor(lang)`, i18n.tsx — fr-BE « 90,2 % », nl-BE « 90,2% »,
// en-GB « 90.2% », as this box's ICU prints them), where `toFixed(1) + "%"`
// printed « 90.2% » to every reader. `null` where the margin has no base.
export const pct1 = (v: number | null | undefined, locale: string): string | null =>
  v == null || !Number.isFinite(v)
    ? null
    : new Intl.NumberFormat(locale, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v / 100);
// EC — AN EMPTY CELL. 214 addendum 1 §10 [RULED 55]: « a cell where no figure
// can be is empty; a dash marks a figure that should be there and cannot be
// given ». « Payé » and « Dû » carry five cells each that can hold no figure
// (three hour columns, two of the three money columns) and printed « — »
// through `HC(null)` / `MC(null)` — « like it's missing something »
// (Laurent, 03/10). The primitive is widened rather than the call bent (rule
// 4): a cell with nothing in it, the money cell's own padding so the row's
// height and the columns' widths do not move. (The empty-string child is what
// jsx-a11y's control-has-associated-label accepts for a bare `<td>`; a
// self-closing cell it reports as an unlabelled control.)
export const EC = () => <td style={{ padding: "5px 8px" }}>{""}</td>;
// TC — a TEXT cell in the money column's dress: a rate, a share, anything
// already formatted. MC's twin with a string where MC has a number; `title`
// is the hover the costs cells carry (what was discarded, what it divides).
export const TC = (v: string | null | undefined, c?: string, fw?: number, title?: string) => (
  <td
    className="mono"
    style={{
      textAlign: "right",
      padding: "5px 8px",
      color: c ?? "inherit",
      fontWeight: fw,
      fontSize: FS.s11_5,
      whiteSpace: "nowrap",
    }}
    title={title}
  >
    {v ?? "—"}
  </td>
);
// SH — a SECTION HEADING ROW inside the recap table: one cell across the
// seven columns, the small uppercase word the tables give their headers, a
// hairline under it. The recap's ten rows stay one list; the heading says
// where the costs line and the activity line begin.
// 214 addendum 1 §11 [RULED 55] — the heading now also opens each GROUP of the
// summary's second part (« Coûts et marge » · « Frais et honoraires » · « Par
// activité »), tables of two and of six columns, so `span` is the second
// parameter (7 where it stood: the recap table's width).
export const SH = (label: ReactNode, span = 7) => (
  <tr>
    <td
      colSpan={span}
      style={{
        padding: "10px 8px 3px",
        fontSize: FS.s9_5,
        fontWeight: 600,
        color: "var(--tm)",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        borderBottom: "1px solid var(--br)",
      }}
    >
      {label}
    </td>
  </tr>
);
